import * as React from 'react'
import type { PlantEdge, PlantUtilityRef } from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { cn } from '../../../lib/utils'
import { SectionHeader } from '../entity/SectionHeader'
import { PlantHref } from '../PlantHref'
import { SessionEntityMark } from '../session-mark-ui'
import { basisStatusClass, formatUtilitySpec } from '../utility-format'

export function UtilitiesSection({
  utilities,
  utilityEdges
}: {
  utilities: PlantUtilityRef[]
  utilityEdges: PlantEdge[]
}): React.ReactElement | null {
  const utilityById = React.useMemo(
    () => new Map(utilities.map((utility) => [utility.id, utility])),
    [utilities]
  )
  const rows = React.useMemo(
    () =>
      utilityEdges
        .filter((edge) => edge.type === 'REQUIRES_UTILITY' && utilityById.has(edge.target))
        .map((edge) => ({ edge, utility: utilityById.get(edge.target)! })),
    [utilityEdges, utilityById]
  )
  if (rows.length === 0) return null

  return (
    <section>
      <SectionHeader
        label="Utilities"
        subtitle={`${rows.length} ${rows.length === 1 ? 'feed' : 'feeds'}`}
      />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-3">
          {rows.map(({ edge, utility }) => (
            <UtilityRow key={`${edge.source}:${edge.target}`} utility={utility} edge={edge} />
          ))}
        </div>
      </div>
    </section>
  )
}

function UtilityRow({
  utility,
  edge
}: {
  utility: PlantUtilityRef
  edge: PlantEdge
}): React.ReactElement {
  const props = edge.props
  const basis = typeof props['basis_status'] === 'string' ? props['basis_status'] : undefined
  const notes = typeof props['notes'] === 'string' ? props['notes'] : undefined
  const spec = formatUtilitySpec(props)

  return (
    <div className="min-w-0">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <PlantHref
            id={utility.id}
            label="Utility"
            node={utility}
            className="text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
          >
            {utility.name}
          </PlantHref>
          {utility.utility_kind && (
            <Badge variant="outline" className="text-10">
              {utility.utility_kind}
            </Badge>
          )}
          {basis && (
            <Badge variant="outline" className={cn('text-10', basisStatusClass(basis))}>
              {basis.replaceAll('_', ' ')}
            </Badge>
          )}
        </div>
        <SessionEntityMark entityId={utility.id} />
      </div>
      {spec && <div className="mt-0.5 font-mono text-12 text-ink-3">{spec}</div>}
      {notes && <div className="mt-0.5 text-12 text-ink-3">{notes}</div>}
    </div>
  )
}
